import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, first, map, shareReplay, tap } from 'rxjs/operators';
import { AccountService, ILicenceDetails, LicenseDetailsStatus } from './account.service';
import { McConnectionsService } from './mc-connections/mc-connections.service';

export class LicenceStatus {
  /// <summary> Inactive license status </summary>
  public static inactive = 'Inactive';

  /// <summary> Expired license status </summary>
  public static expired = 'Expired';

  /// <summary> Active license status </summary>
  public static active = 'Active';
}

export function toLicenseDetailsStatus(licenseStatus: string): LicenseDetailsStatus {
  switch (licenseStatus) {
    case LicenceStatus.active: {
      return LicenseDetailsStatus.active;
    }
    case LicenceStatus.expired: {
      return LicenseDetailsStatus.expired;
    }
    case LicenceStatus.inactive: {
      return LicenseDetailsStatus.inactive;
    }
    default: {
      return LicenseDetailsStatus.invalid;
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class LicenceInfoService {
  private licenceDetails: ILicenceDetails;
  private licenceDetails$: Observable<ILicenceDetails>;
  private licenseStatuses = new Map<number, Observable<LicenseDetailsStatus>>();
  private defaultConnectionId : number = 0;

  constructor(private accountService: AccountService, mcConnectionsService: McConnectionsService) {
    mcConnectionsService.mcConnection$.pipe(first()).subscribe(connections => {
      const defaultConnection = connections.find(c => c.isDefault);
      if (defaultConnection) {
        this.defaultConnectionId = defaultConnection.id;
      }
    })
  }

  public getLicenceInfoPromise = (): Promise<ILicenceDetails> => {
    return this.getLicenceInformation().toPromise();
  };

  public isLicenseActive(): boolean {
    if (!this.licenceDetails) {
      this.getLicenceInformation();
    }
    return this.licenceDetails && this.licenceDetails.status === LicenceStatus.active;
  }

  public isLicenseActiveAsync(): Promise<boolean> {
    return this.getLicenceInformation().pipe(first(), map(this._isLicenseActive)).toPromise();
  }

  public invalidateLicenceInformation = (): void => {
    this.licenceDetails$ = null;
  };

  public getLicenseStatus$ = (connectionId?: number): Observable<LicenseDetailsStatus> => {
    const mapKey = this.getLicenseStatusKey(connectionId);
    let licenseStatus$ = this.licenseStatuses.get(mapKey);
    if (licenseStatus$) {
      return licenseStatus$
    }
    licenseStatus$ = this.accountService.getLicenceDetailsStatus(connectionId).pipe(
      catchError(_ => of(LicenseDetailsStatus.inactive)), 
      shareReplay(1));
    this.licenseStatuses.set(mapKey, licenseStatus$);
    return licenseStatus$;
  };

  public getLicenceInformation = (): Observable<ILicenceDetails> => {
    if (!this.licenceDetails$) {
      const licenseObservable = this.accountService.getLicenceDetails();
      this.licenceDetails$ = licenseObservable.pipe(
        tap(licenseDetails => {
          if (licenseDetails) {
            this.licenseStatuses.set(this.getLicenseStatusKey(), of(toLicenseDetailsStatus(licenseDetails.status)));
          }
        }),
        map(licenceDetails => {
          if (licenceDetails.status !== 'Inactive') {
            // current date should fall into activation and expiration date
            let currentDate = new Date();
            const startDate = new Date(licenceDetails.licenseStartDate);
            const endDate = new Date(licenceDetails.licenseEndDate);

            // if current date is grater than the expiration date,  current date is equal to the expiration date
            if (currentDate > endDate) {
              currentDate = endDate;
            }

            // if current date is earlier than the activation date, use activation date
            if (currentDate < startDate) {
              currentDate = startDate;
            }

            const totalLengthInDays = millisecondsToDays(endDate.getTime() - startDate.getTime());

            const daysUsed = millisecondsToDays(currentDate.getTime() - startDate.getTime());

            let daysRemaining = millisecondsToDays(endDate.getTime() - currentDate.getTime());

            if (daysRemaining < 0) {
              daysRemaining = 0;
            }

            licenceDetails.$totalLengthInDays = Math.floor(totalLengthInDays);
            licenceDetails.$daysUsed = Math.floor(daysUsed);
            licenceDetails.$daysRemaining = Math.ceil(daysRemaining);
            this.licenceDetails = licenceDetails;
          }
          return licenceDetails;
        }),
        first(),
        shareReplay(1)
      );
    }

    return this.licenceDetails$;
  };

  private _isLicenseActive(licenseDetails: ILicenceDetails): boolean {
    return licenseDetails && licenseDetails.status === LicenceStatus.active;
  }

  private getLicenseStatusKey(connectionId: number | null = null): number {
    if (connectionId === null || connectionId === undefined) {
      return this.defaultConnectionId;
    }

    return connectionId;
  }
}

const millisecondsToDays = (milliseconds: number): number => {
  return milliseconds / 1000 / 60 / 60 / 24;
};
